import { useNavigate } from 'react-router-dom';
import Box from '@mui/material/Box';
import Card from '@components/base/card/card.component.tsx';
import CardHeader from '@components/base/card/card-header.component.tsx';
import { CircularLoaderContainer } from '@components/base/circular-loader-container.component.tsx';
import {
  Divider,
  List,
  ListItem,
  ListItemText,
  Typography,
} from '@mui/material';
import { formatMoneyString } from '@utils/format-money-string.ts';
import { useMemo } from 'react';
import { theme } from '@/config/theme';
import { useCart } from '@/api/carts/carts.hooks.ts';
import { useTranslation } from 'react-i18next';

interface CartSummaryCardProps {
  children: React.ReactNode;
}

export function CartSummaryCard({ children }: CartSummaryCardProps) {
  const navigate = useNavigate();
  const { t } = useTranslation('cart');
  const { data, isPending, isLoading } = useCart();
  const shippingType = useMemo(
    () => data?.priceCalculation.shippingType,
    [data],
  );
  const cartItems = useMemo(() => data?.cartItems || [], [data]);
  const priceCalculation = useMemo(() => data?.priceCalculation, [data]);
  const vatGroupCalculations = useMemo(
    () => priceCalculation?.vatGroups,
    [priceCalculation],
  );
  const vatGroupCodes = useMemo(
    () => Object.keys(vatGroupCalculations || {}),
    [vatGroupCalculations],
  );
  const totalAmount = useMemo(
    () => priceCalculation?.totalAmount || 0,
    [priceCalculation],
  );
  const totalNetAmount = useMemo(
    () => priceCalculation?.totalNetAmount || 0,
    [priceCalculation],
  );

  return (
    <Card
      sx={{
        width: '100%',
      }}
      elevation={2}
    >
      <CardHeader title={t('summary.title')} />
      {isLoading ? (
        <CircularLoaderContainer />
      ) : (
        <List>
          {shippingType ? (
            <>
              <ListItem>
                <ListItemText
                  primary={shippingType.name}
                  slotProps={{ primary: { variant: 'subtitle2' } }}
                />

                <ListItemText
                  slotProps={{ primary: { variant: 'subtitle2' } }}
                  primary={formatMoneyString(shippingType.shippingCost)}
                  sx={{ textAlign: 'end' }}
                />
              </ListItem>
              <Divider />
            </>
          ) : null}
          <ListItem>
            <ListItemText
              primary={t('summary.subtotal')}
              slotProps={{ primary: { variant: 'subtitle2' } }}
            />
            <ListItemText
              slotProps={{ primary: { variant: 'subtitle2' } }}
              primary={formatMoneyString(totalNetAmount)}
              sx={{ textAlign: 'end' }}
            />
          </ListItem>
          {vatGroupCodes.map((vatGroupCode) => {
            const vatGroup = vatGroupCalculations?.[vatGroupCode];
            const totalVatAmount = vatGroup?.totalVatAmount || 0;
            return (
              <Box key={vatGroupCode}>
                <Divider />
                <ListItem>
                  <ListItemText
                    primary={vatGroup?.name}
                    slotProps={{ primary: { variant: 'subtitle2' } }}
                  />
                  <ListItemText
                    slotProps={{ primary: { variant: 'subtitle2' } }}
                    primary={formatMoneyString(totalVatAmount)}
                    sx={{ textAlign: 'end' }}
                  />
                </ListItem>
              </Box>
            );
          })}
          <Divider />
          <ListItem>
            <ListItemText
              primary={t('summary.total')}
              slotProps={{ primary: { variant: 'h6' } }}
            />
            <ListItemText
              primary={formatMoneyString(totalAmount)}
              slotProps={{ primary: { variant: 'h6' } }}
              sx={{ textAlign: 'end' }}
            />
          </ListItem>
          <Divider />
          <Typography
            variant={'body2'}
            sx={{ p: theme.spacing(2), color: theme.palette.warning.main }}
          >
            {t('summary.shippingNotice')}
            <br /> {t('summary.shippingBillingNotice')}
          </Typography>
        </List>
      )}
      {children}
    </Card>
  );
}
